/**
 * Custom hook for deleting conversations with optimistic updates
 * Follows the same patterns as useRenameConversation for consistency
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  deleteConversation,
  DeleteConversationParams,
  DeleteConversationResponse,
  conversationQueryKeys,
  ConversationApiError,
  type Conversation,
} from '../api/conversations';
import { ApiError } from '../api/base/errors';
import { useConversationStore } from '../stores/conversationStore';
import { useUserSettingsStore } from '../stores/userSettingsStore';
import { showNotification } from '../components/Notification';


/**
 * Optimistic update context for rollback on error
 */
interface DeleteOptimisticUpdateContext {
  previousConversationLists: Conversation[] | undefined;
  previousConversationDetail: any;
  deletedConversation: Conversation | undefined;
  conversationId: string;
  wasSelected: boolean;
  wasCurrent: boolean;
}

/**
 * Core mutation hook for deleting conversations
 * Implements optimistic updates with rollback following TanStack Query best practices
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Get store actions for optimistic updates
  const removeConversationAction = useConversationStore(
    (state) => state.removeConversation
  );
  const addConversationAction = useConversationStore(
    (state) => state.addConversation
  );
  const setSelectedConversation = useConversationStore(
    (state) => state.setSelectedConversation
  );
  const setCurrentConversationId = useConversationStore(
    (state) => state.setCurrentConversationId
  );
  const selectedConversationId = useConversationStore(
    (state) => state.selectedConversationId
  );
  const currentConversationId = useConversationStore(
    (state) => state.currentConversationId
  );

  return useMutation({
    mutationFn: (params: DeleteConversationParams) => deleteConversation(params),

    // Optimistic updates following TanStack Query patterns
    onMutate: async (variables): Promise<DeleteOptimisticUpdateContext> => {
      const { conversationId } = variables;

      // Cancel any outgoing refetches to prevent overwrites
      await queryClient.cancelQueries({
        queryKey: conversationQueryKeys.list({})
      });
      await queryClient.cancelQueries({
        queryKey: conversationQueryKeys.detail(conversationId)
      });

      // Snapshot previous state for rollback
      const previousConversationLists = queryClient.getQueryData<Conversation[]>(
        conversationQueryKeys.list({})
      );

      const previousConversationDetail = queryClient.getQueryData(
        conversationQueryKeys.detail(conversationId)
      );

      // Find the conversation being deleted for rollback
      const deletedConversation = previousConversationLists?.find(
        (conv) => conv.conversationID === conversationId
      );

      // Track if this conversation was selected or current
      const wasSelected = selectedConversationId === conversationId;
      const wasCurrent = currentConversationId === conversationId;

      // Optimistically remove from conversation list
      if (previousConversationLists) {
        queryClient.setQueryData(
          conversationQueryKeys.list({}),
          (old: Conversation[] | undefined) => {
            if (!old) return old;
            return old.filter(conv => conv.conversationID !== conversationId);
          }
        );
      }

      // Remove conversation detail from cache
      queryClient.removeQueries({
        queryKey: conversationQueryKeys.detail(conversationId)
      });

      // Update Zustand store optimistically
      removeConversationAction(conversationId);

      return {
        previousConversationLists,
        previousConversationDetail,
        deletedConversation,
        conversationId,
        wasSelected,
        wasCurrent,
      };
    },

    // Success handling - minimal work since optimistic updates are already applied
    onSuccess: (response: DeleteConversationResponse, variables: DeleteConversationParams, context?: DeleteOptimisticUpdateContext) => {
      // Handle navigation if the deleted conversation was current
      if (context?.wasCurrent) {
        // Navigate to home page when current conversation is deleted
        navigate({ to: '/' });
      }

    },

    // Error handling with complete rollback
    onError: (error: Error, variables: DeleteConversationParams, context?: DeleteOptimisticUpdateContext) => {
      if (!context) return;

      // Rollback conversation list
      if (context.previousConversationLists) {
        queryClient.setQueryData(
          conversationQueryKeys.list({}),
          context.previousConversationLists
        );
      }

      // Rollback conversation detail
      if (context.previousConversationDetail) {
        queryClient.setQueryData(
          conversationQueryKeys.detail(context.conversationId),
          context.previousConversationDetail
        );
      }

      // Rollback Zustand store - re-add the conversation
      if (context.deletedConversation) {
        addConversationAction(context.deletedConversation);

        // Restore selection state
        if (context.wasSelected) {
          setSelectedConversation(context.conversationId);
        }
        if (context.wasCurrent) {
          setCurrentConversationId(context.conversationId);
        }
      }

      // Log error for debugging
      console.error('Failed to delete conversation:', {
        conversationId: variables.conversationId,
        error: error.message,
      });
    },

    // Invalidate queries on completion (success or error)
    onSettled: (data, error, variables) => {
      // Mark conversation queries as stale to trigger eventual consistency
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.list({}),
        refetchType: 'none' // Mark as stale but don't refetch immediately
      });
    },

    // Retry configuration following established patterns
    retry: (failureCount: number, error: any) => {
      // Don't retry authentication errors
      if (error instanceof ConversationApiError && error.status === 401) {
        return false;
      }

      // Don't retry client errors (4xx)
      if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
        return false;
      }

      // Retry network errors and server errors up to 2 times
      return failureCount < 2;
    },

    // Exponential backoff for retries
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

/**
 * High-level hook that provides a clean interface for components
 * Follows the same pattern as useConversationRenameActions
 */
export function useConversationDeleteActions() {
  const deleteMutation = useDeleteConversation();
  const { isConversationPinned, removePinnedConversation } = useUserSettingsStore();

  const deleteConversation = useCallback(async (conversationId: string) => {
    if (!conversationId.trim()) {
      throw new Error('Conversation ID is required');
    }

    try {
      const response = await deleteMutation.mutateAsync({
        conversationId: conversationId.trim(),
      });

      // If the conversation was pinned, remove it from the pinned list
      if (isConversationPinned(conversationId.trim())) {
        await removePinnedConversation(conversationId.trim());
      }

      showNotification("Your chat has been deleted");

      return response;
    } catch (error) {
      // Error is handled by mutation's onError
      throw error;
    }
  }, [deleteMutation]);

  return {
    deleteConversation,
    isDeleting: deleteMutation.isPending,
    error: deleteMutation.error,
    isSuccess: deleteMutation.isSuccess,
    isError: deleteMutation.isError,
    reset: deleteMutation.reset,
    // Helper computed properties
    canDelete: !deleteMutation.isPending,
  };
}
